import { NavLink, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Loading, Pill } from "@/components/Ui";

export function RequireRole({ role, children }) {
  const { user, loading } = useAuth();
  if (loading) return <Loading label="Checking your session…" />;
  if (!user) return <Navigate to={`/login?next=${encodeURIComponent(window.location.pathname)}`} replace />;
  if (role && user.role !== role) return <Navigate to="/" replace />;
  return children;
}

export default function DashboardShell({ title, subtitle, nav, children, testId }) {
  const { user } = useAuth();
  const company = user?.company;

  return (
    <div className="mx-auto max-w-[1240px] px-5 py-8" data-testid={testId}>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[#147D78]">{subtitle}</p>
          <h1 className="mt-2 font-display text-2xl font-black tracking-tight text-[#102A43] sm:text-3xl">
            {title}
          </h1>
          <p className="mt-1 text-sm text-[#667788]">
            {company?.name ? `${company.name} · ` : ""}
            {user?.email}
          </p>
        </div>
        {company && (
          <div className="flex flex-wrap gap-2">
            <Pill
              tone={company.verification_status === "verified" ? "ok" : company.verification_status === "rejected" ? "bad" : "warn"}
              testId="verification-pill"
            >
              {company.verification_status === "verified" ? "Verified company" : `Verification ${company.verification_status}`}
            </Pill>
            {company.type === "supplier" && (
              <Pill tone="gold" testId="plan-pill">
                {(company.subscription_plan || "founding").toUpperCase()} plan
              </Pill>
            )}
          </div>
        )}
      </div>

      <div className="mt-7 grid gap-6 lg:grid-cols-[220px_1fr]">
        <nav className="flex gap-2 overflow-x-auto border-b border-[#DFE6ED] pb-3 lg:flex-col lg:overflow-visible lg:border-b-0 lg:border-r lg:pb-0 lg:pr-4">
          {nav.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={n.testId}
              className={({ isActive }) =>
                `shrink-0 rounded-[9px] px-3.5 py-2.5 text-sm font-semibold transition-colors ${
                  isActive ? "bg-[#102A43] text-white" : "text-[#667788] hover:bg-[#eef2f7]"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="min-w-0">{children}</div>
      </div>
    </div>
  );
}
